// AUTO-GENERATED from content/zones/ — do not edit by hand

export interface ZoneSpawnEntry {
  mobId: string;
  x: number;
  y: number;
  count: number;
  respawnMs: number;
  /** Patrol mobs: the stops it walks between (`patrol_route` in the zone TOML). */
  patrolRoute?: { x: number; y: number }[];
  /** A level override (`level` in the zone TOML) and the curve's hp and xp for it. */
  level?: number;
  hp?: number;
  xp?: number;
}

/**
 * Placement of one dialogue/shop/quest NPC within a zone. Declared via
 * `[[npc_spawns]]` in content/zones/*.toml. x/y/facing are optional — when
 * omitted, the NPC spawns at its authored home position from NPC_CONFIG.
 * npcId is an FK into the NPC roster (content/npcs/*.toml → NPC_CONFIG).
 */
export interface ZoneNpcSpawnEntry {
  npcId: string;
  x?: number;
  y?: number;
  facing?: 'left' | 'right';
}

/**
 * Placement of one fixed quest-collectible pickup within a zone. Declared via
 * `[[collectible_spawns]]` in content/zones/*.toml (Hollow Coil Lane D S2).
 * itemId is an FK into ITEM_DATABASE. x/y is the pickup visual's CENTER
 * (y ≈ platform.y - 24 to hover on a platform). respawnMs > 0 → the pickup
 * reappears that long after collection (0 = one-shot). icon is the world
 * glyph (emoji — the interactable visual renders text); displayName overrides
 * the item name on the pickup + toast; lore carries readable-page prose in
 * interactable metadata for a future journal/reading UI.
 */
export interface ZoneCollectibleSpawnEntry {
  itemId: string;
  x: number;
  y: number;
  respawnMs: number;
  icon?: string;
  displayName?: string;
  lore?: string;
}

export interface ZoneConfig {
  zoneId: string;
  levelRange: [number, number];
  zoneType: 'town' | 'field' | 'dungeon' | 'arena';
  /** Deliberately mob-free (`mob_free = true` in the zone TOML) — content
   *  smoke tests exempt it from the ≥1-spawn rule. */
  mobFree?: boolean;
  /** Draft zone (`draft = true` in the zone TOML): playable, but left off the
   *  World Map until it is finished. */
  draft?: boolean;
  spawns: ZoneSpawnEntry[];
  npcSpawns: ZoneNpcSpawnEntry[];
  /** Optional — only zones that place fixed collectibles carry this. */
  collectibleSpawns?: ZoneCollectibleSpawnEntry[];
}

export const ZONE_CONFIGS: Record<string, ZoneConfig> = {
  'abandoned_camp': {
    zoneId: 'abandoned_camp',
    levelRange: [6, 8] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'stump', x: 640, y: 1604, count: 1, respawnMs: 25000 },
      { mobId: 'stump', x: 1536, y: 1636, count: 1, respawnMs: 25000 },
      { mobId: 'stump', x: 1840, y: 1636, count: 1, respawnMs: 25000 },
      { mobId: 'wild_boar', x: 2448, y: 1582, count: 1, respawnMs: 20000 },
      { mobId: 'wild_boar', x: 3072, y: 1582, count: 1, respawnMs: 20000 },
      { mobId: 'shadow_bat', x: 3456, y: 1597, count: 1, respawnMs: 24000 },
      { mobId: 'shadow_bat', x: 5232, y: 1725, count: 1, respawnMs: 24000 },
      { mobId: 'shadow_bat', x: 5504, y: 1725, count: 1, respawnMs: 24000 },
      { mobId: 'stump', x: 8064, y: 1604, count: 1, respawnMs: 25000 },
    ],
    npcSpawns: [
      { npcId: 'forest_sawkeeper_hetta', x: 7168, y: 1678, facing: 'right' },
    ],
    collectibleSpawns: [
      { itemId: 'forest_tool_roll', x: 6848, y: 1320, respawnMs: 30000, icon: '🧰', displayName: 'Hetta\'s Tool Roll', lore: 'A pruning saw and grafting knife lie wrapped in waxed canvas. The camp once tended the grove as well as taking timber from it.' },
    ],
  },
  'amber_grove': {
    zoneId: 'amber_grove',
    levelRange: [9, 15] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'deer', x: 950, y: 1252, count: 2, respawnMs: 20000 },
      { mobId: 'gearwright_logger', x: 1300, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'gearwright_logger', x: 1700, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'corrupted_dryad', x: 1235, y: 765, count: 2, respawnMs: 30000 },
      { mobId: 'darkwood_treant', x: 1560, y: 638, count: 2, respawnMs: 30000, patrolRoute: [{ x: 1400, y: 638 }, { x: 1690, y: 638 }] },
      { mobId: 'darkwood_treant', x: 2160, y: 688, count: 2, respawnMs: 30000, patrolRoute: [{ x: 2020, y: 688 }, { x: 2260, y: 688 }] },
      { mobId: 'elder_treant', x: 1870, y: 552, count: 1, respawnMs: 300000 },
      { mobId: 'rabbit', x: 1950, y: 1264, count: 3, respawnMs: 20000 },
      { mobId: 'amberbark_treant', x: 2420, y: 803, count: 2, respawnMs: 30000 },
      { mobId: 'amberbark_treant', x: 2640, y: 923, count: 2, respawnMs: 30000 },
      { mobId: 'scarecrow_golem', x: 2250, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'scarecrow_golem', x: 2600, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'scarecrow_warden', x: 2850, y: 1235, count: 2, respawnMs: 30000 },
      { mobId: 'duchess', x: 2960, y: 1235, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'amber_grovekeeper', x: 600, y: 1250, facing: 'right' },
      { npcId: 'amber_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'ancient_temple': {
    zoneId: 'ancient_temple',
    levelRange: [10, 14] as [number, number],
    zoneType: 'dungeon',
    spawns: [
      { mobId: 'cursed_armor', x: 500, y: 1650, count: 3, respawnMs: 90000 },
      { mobId: 'shadow_stalker', x: 1000, y: 1650, count: 3, respawnMs: 25000 },
      { mobId: 'ancient_treant', x: 1500, y: 950, count: 2, respawnMs: 90000 },
      { mobId: 'crystal_elemental', x: 2000, y: 1150, count: 2, respawnMs: 30000 },
      { mobId: 'war_golem', x: 2500, y: 1650, count: 2, respawnMs: 120000 },
      { mobId: 'stone_golem', x: 2900, y: 950, count: 2, respawnMs: 60000 },
      { mobId: 'shadow_stalker', x: 2200, y: 1150, count: 3, respawnMs: 25000 },
      { mobId: 'cursed_armor', x: 1750, y: 1650, count: 2, respawnMs: 90000 },
      { mobId: 'crystal_elemental', x: 750, y: 950, count: 2, respawnMs: 30000 },
    ],
    npcSpawns: [],
  },
  'bloodmoon_reach': {
    zoneId: 'bloodmoon_reach',
    levelRange: [16, 24] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'ember_vigil_zealot', x: 1300, y: 1235, count: 2, respawnMs: 30000 },
      { mobId: 'ember_vigil_zealot', x: 1350, y: 835, count: 2, respawnMs: 30000 },
      { mobId: 'mindless_zombie', x: 1650, y: 1238, count: 3, respawnMs: 25000 },
      { mobId: 'mindless_zombie', x: 2100, y: 1238, count: 3, respawnMs: 25000 },
      { mobId: 'plague_rat', x: 1900, y: 1264, count: 3, respawnMs: 25000 },
      { mobId: 'plague_rat', x: 2150, y: 1124, count: 2, respawnMs: 25000 },
      { mobId: 'rat', x: 2600, y: 1276, count: 2, respawnMs: 25000 },
      { mobId: 'skeletal_warrior', x: 1700, y: 705, count: 2, respawnMs: 30000 },
      { mobId: 'skeletal_warrior', x: 2150, y: 835, count: 2, respawnMs: 30000 },
      { mobId: 'skeletal_mage', x: 2480, y: 965, count: 2, respawnMs: 30000 },
      { mobId: 'skeletal_mage', x: 1900, y: 705, count: 1, respawnMs: 30000 },
      { mobId: 'skeletal_mage', x: 1330, y: 705, count: 1, respawnMs: 30000 },
      { mobId: 'arch_lich_malakar', x: 1580, y: 530, count: 1, respawnMs: 600000 },
      { mobId: 'ember_vigil_aldous', x: 2700, y: 1092, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'blood_gravewatch', x: 600, y: 1250, facing: 'right' },
      { npcId: 'blood_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'brackenmoor': {
    zoneId: 'brackenmoor',
    levelRange: [4, 6] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'wild_boar', x: 1400, y: 940, count: 1, respawnMs: 18000 },
      { mobId: 'wild_boar', x: 1900, y: 940, count: 1, respawnMs: 18000 },
      { mobId: 'wild_boar', x: 2400, y: 940, count: 1, respawnMs: 18000 },
      { mobId: 'wild_boar', x: 2900, y: 940, count: 1, respawnMs: 18000 },
      { mobId: 'giant_bee', x: 1500, y: 860, count: 1, respawnMs: 14000 },
      { mobId: 'giant_bee', x: 1800, y: 770, count: 1, respawnMs: 14000 },
      { mobId: 'giant_bee', x: 2000, y: 770, count: 1, respawnMs: 14000 },
      { mobId: 'giant_bee', x: 2250, y: 680, count: 1, respawnMs: 14000 },
      { mobId: 'giant_bee', x: 2450, y: 680, count: 1, respawnMs: 14000 },
      { mobId: 'mushroom', x: 3400, y: 1030, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 4100, y: 1030, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 4800, y: 1030, count: 1, respawnMs: 20000 },
      { mobId: 'shadow_bat', x: 3450, y: 925, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 3780, y: 815, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 4200, y: 725, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 4640, y: 855, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 10600, y: 955, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 10930, y: 845, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 11350, y: 755, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 11780, y: 885, count: 1, respawnMs: 18000 },
      { mobId: 'evil_eye', x: 7200, y: 1060, count: 1, respawnMs: 20000 },
      { mobId: 'evil_eye', x: 8000, y: 1150, count: 1, respawnMs: 20000 },
      { mobId: 'evil_eye', x: 9000, y: 1060, count: 1, respawnMs: 20000 },
      { mobId: 'evil_eye', x: 9900, y: 980, count: 1, respawnMs: 20000 },
    ],
    npcSpawns: [
      { npcId: 'ranger_cole', x: 820, y: 1000, facing: 'right' },
    ],
    collectibleSpawns: [
      { itemId: 'keepers_journal_page', x: 1500, y: 876, respawnMs: 30000, icon: '📜', displayName: 'Journal Page I — The Lamps of Tidewend', lore: 'Third day of spring, year thirty-nine of my keeping. The wick wants trimming twice a night in winter, but I don\'t mind the climb — two hundred and twelve steps, and from the gallery you can watch every lamp in Tidewend come on, one by one, like the village answering the Light. Wynn waved from the docks this morning. She thinks I can\'t see her from up here. I can see everything from up here. As long as the Coil turns, nobody on that black water is alone. Neither am I.' },
      { itemId: 'keepers_journal_page', x: 3450, y: 936, respawnMs: 30000, icon: '📜', displayName: 'Journal Page II — The Dark Under the Door', lore: 'The oil is good. The glass is clean. And still the Light gutters — three nights now, always at the turn of the tide. There is a dark that comes up the stair from the undercroft. Not damp. Not shadow. It gathers under the door like a tide deciding, and when I hold the lamp to the keyhole it goes. I have not slept. If I sleep, it climbs. I wrote to the mainland for stronger oil. I did not tell the village. Why frighten them? The Light still turns. While it turns, I keep it.' },
      { itemId: 'keepers_journal_page', x: 10600, y: 966, respawnMs: 30000, icon: '📜', displayName: 'Journal Page III — A Keeper Keeps', lore: 'cold in the bones of the tower now. the coil turns but the light goes nowhere — it stops at the glass like it is ashamed. i went down to find where the dark gets in and the undercroft door — there is no door anymore. there is a mouth. it said my name in my mother\'s voice. so be it. i will wear the storm-plate down and stop the gap with my own two hands. if the lamps of tidewend go out one by one, do not be afraid. that is only me, gathering them somewhere safe. i am keeping them. a keeper keeps. a keeper k—' },
    ],
  },
  'coil_undercroft': {
    zoneId: 'coil_undercroft',
    levelRange: [6, 8] as [number, number],
    zoneType: 'dungeon',
    spawns: [
      { mobId: 'dark_stump', x: 570, y: 455, count: 1, respawnMs: 28000 },
      { mobId: 'dark_stump', x: 790, y: 455, count: 1, respawnMs: 28000 },
      { mobId: 'dark_stump', x: 1010, y: 455, count: 1, respawnMs: 28000 },
      { mobId: 'shadow_bat', x: 1210, y: 705, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 1460, y: 705, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 1710, y: 705, count: 1, respawnMs: 22000 },
      { mobId: 'evil_eye', x: 560, y: 920, count: 1, respawnMs: 24000 },
      { mobId: 'evil_eye', x: 800, y: 920, count: 1, respawnMs: 24000 },
      { mobId: 'evil_eye', x: 1040, y: 920, count: 1, respawnMs: 24000 },
      { mobId: 'shadow_bat', x: 1230, y: 1125, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 1420, y: 1125, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 1610, y: 1125, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 1800, y: 1125, count: 1, respawnMs: 22000 },
      { mobId: 'hollow_keeper', x: 240, y: 1290, count: 1, respawnMs: 120000 },
    ],
    npcSpawns: [],
  },
  'crystal_caverns': {
    zoneId: 'crystal_caverns',
    levelRange: [8, 15] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'cave_bat', x: 500, y: 1350, count: 4, respawnMs: 15000 },
      { mobId: 'crystal_spider', x: 800, y: 1650, count: 3, respawnMs: 25000 },
      { mobId: 'stone_golem', x: 600, y: 1650, count: 2, respawnMs: 60000 },
      { mobId: 'crystal_spider', x: 1400, y: 1650, count: 3, respawnMs: 25000 },
      { mobId: 'cursed_armor', x: 2200, y: 1650, count: 3, respawnMs: 90000 },
      { mobId: 'gem_golem', x: 2500, y: 1250, count: 2, respawnMs: 90000 },
      { mobId: 'cave_bat', x: 1800, y: 1050, count: 3, respawnMs: 15000 },
      { mobId: 'void_wraith', x: 3100, y: 950, count: 1, respawnMs: 180000 },
      { mobId: 'crystal_elemental', x: 1600, y: 1650, count: 2, respawnMs: 30000 },
      { mobId: 'shadow_stalker', x: 2000, y: 1050, count: 2, respawnMs: 25000 },
      { mobId: 'obsidian_sentinel', x: 2600, y: 1650, count: 1, respawnMs: 120000 },
      { mobId: 'flame_imp', x: 1200, y: 1350, count: 3, respawnMs: 18000 },
    ],
    npcSpawns: [],
  },
  'dark_forest': {
    zoneId: 'dark_forest',
    levelRange: [3, 9] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'mushroom', x: 580, y: 1250, count: 3, respawnMs: 20000 },
      { mobId: 'stump', x: 900, y: 1250, count: 4, respawnMs: 25000 },
      { mobId: 'evil_eye', x: 600, y: 950, count: 2, respawnMs: 20000 },
      { mobId: 'shadow_bat', x: 1300, y: 750, count: 3, respawnMs: 20000 },
      { mobId: 'dark_stump', x: 2440, y: 1250, count: 2, respawnMs: 45000 },
      { mobId: 'blightroot_alpha', x: 2510, y: 1204, count: 1, respawnMs: 90000 },
      { mobId: 'cursed_armor', x: 2700, y: 1050, count: 1, respawnMs: 90000, patrolRoute: [{ x: 2615, y: 1025 }, { x: 2930, y: 1025 }] },
      { mobId: 'giant_bee', x: 500, y: 900, count: 3, respawnMs: 15000 },
      { mobId: 'forest_troll', x: 1480, y: 1250, count: 1, respawnMs: 20000 },
      { mobId: 'forest_troll', x: 1810, y: 1250, count: 1, respawnMs: 20000 },
      { mobId: 'forest_troll', x: 2140, y: 1250, count: 1, respawnMs: 20000 },
      { mobId: 'wild_boar', x: 1100, y: 1250, count: 3, respawnMs: 18000 },
      { mobId: 'giant_bee', x: 800, y: 950, count: 3, respawnMs: 15000 },
    ],
    npcSpawns: [],
  },
  'deep_woods': {
    zoneId: 'deep_woods',
    levelRange: [6, 8] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'stump', x: 800, y: 1604, count: 1, respawnMs: 25000 },
      { mobId: 'stump', x: 1664, y: 1668, count: 1, respawnMs: 25000 },
      { mobId: 'shadow_bat', x: 2192, y: 1693, count: 1, respawnMs: 24000 },
      { mobId: 'wild_boar', x: 2688, y: 1742, count: 1, respawnMs: 20000 },
      { mobId: 'stump', x: 3008, y: 1732, count: 1, respawnMs: 25000 },
      { mobId: 'wild_boar', x: 3296, y: 1742, count: 1, respawnMs: 20000 },
      { mobId: 'forest_troll', x: 3904, y: 1626, count: 1, respawnMs: 20000 },
      { mobId: 'forest_troll', x: 4384, y: 1626, count: 1, respawnMs: 20000 },
      { mobId: 'shadow_bat', x: 6496, y: 1661, count: 1, respawnMs: 24000 },
      { mobId: 'stump', x: 7488, y: 1700, count: 1, respawnMs: 25000 },
      { mobId: 'wild_boar', x: 7936, y: 1710, count: 1, respawnMs: 20000 },
      { mobId: 'wild_boar', x: 8512, y: 1614, count: 1, respawnMs: 20000 },
    ],
    npcSpawns: [
      { npcId: 'forest_scout_iven', x: 384, y: 1614, facing: 'right' },
    ],
  },
  'drowned_road': {
    zoneId: 'drowned_road',
    levelRange: [3, 4] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'slime', x: 1300, y: 1060, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 1750, y: 1060, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 2200, y: 1060, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 2000, y: 1060, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 4000, y: 970, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 4150, y: 970, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 4300, y: 970, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 4850, y: 970, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 8900, y: 1050, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 9400, y: 1050, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 9900, y: 1050, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 10400, y: 1050, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 5200, y: 1030, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 5800, y: 1120, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 6400, y: 1120, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 7200, y: 1040, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 8000, y: 960, count: 1, respawnMs: 9000 },
      { mobId: 'mushroom', x: 12700, y: 1050, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 13300, y: 1050, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 13900, y: 1050, count: 1, respawnMs: 18000 },
    ],
    npcSpawns: [],
  },
  'drowned_road_hush_nook': {
    zoneId: 'drowned_road_hush_nook',
    levelRange: [3, 4] as [number, number],
    zoneType: 'field',
    mobFree: true,
    spawns: [],
    npcSpawns: [
      { npcId: 'mallow_wick', x: 520, y: 670, facing: 'left' },
    ],
  },
  'duskfall_vale': {
    zoneId: 'duskfall_vale',
    levelRange: [10, 18] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'wolf', x: 1300, y: 1240, count: 3, respawnMs: 25000 },
      { mobId: 'wolf', x: 1750, y: 1240, count: 2, respawnMs: 25000 },
      { mobId: 'rabid_wolf', x: 1350, y: 886, count: 2, respawnMs: 25000 },
      { mobId: 'rabid_wolf', x: 2200, y: 1246, count: 2, respawnMs: 25000 },
      { mobId: 'dire_wolf', x: 1600, y: 992, count: 3, respawnMs: 30000 },
      { mobId: 'moonhowler_lurker', x: 1950, y: 871, count: 1, respawnMs: 30000 },
      { mobId: 'moonhowler_lurker', x: 2200, y: 751, count: 2, respawnMs: 30000 },
      { mobId: 'moonhowler_lurker', x: 2700, y: 991, count: 1, respawnMs: 30000 },
      { mobId: 'moonhowler_lurker', x: 2760, y: 1231, count: 1, respawnMs: 30000 },
      { mobId: 'grimfang_the_ravenous', x: 1100, y: 724, count: 1, respawnMs: 300000 },
      { mobId: 'moonhowler_tobin', x: 2500, y: 857, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'dusk_lampwarden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'dusk_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'first_field': {
    zoneId: 'first_field',
    levelRange: [1, 2] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'pig', x: 1450, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 1700, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 1950, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2200, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2450, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2700, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2950, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 3200, y: 1210, count: 1, respawnMs: 12000 },
      { mobId: 'snail', x: 3650, y: 1320, count: 1, respawnMs: 8000 },
      { mobId: 'snail', x: 4100, y: 1320, count: 1, respawnMs: 8000 },
      { mobId: 'snail', x: 4550, y: 1400, count: 1, respawnMs: 8000 },
      { mobId: 'slime', x: 5550, y: 1230, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 6150, y: 1320, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 6550, y: 1320, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 7700, y: 1290, count: 1, respawnMs: 8000 },
      { mobId: 'snail', x: 8200, y: 1290, count: 1, respawnMs: 8000 },
      { mobId: 'snail', x: 8700, y: 1290, count: 1, respawnMs: 8000 },
      { mobId: 'slime', x: 9550, y: 1200, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 10000, y: 1200, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 10450, y: 1200, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 11600, y: 1270, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 12100, y: 1270, count: 1, respawnMs: 10000 },
      { mobId: 'slime', x: 12600, y: 1270, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 9500, y: 1020, count: 1, respawnMs: 8000 },
      { mobId: 'wild_boar', x: 10300, y: 960, count: 1, respawnMs: 12000 },
    ],
    npcSpawns: [
      { npcId: 'field_warden', x: 14550, y: 1190, facing: 'left' },
    ],
    collectibleSpawns: [
      { itemId: 'ancient_relic', x: 3500, y: 1336, respawnMs: 30000, icon: '🏺', lore: 'A warm-cut stone rests where the pasture gives way to the moonlit basin.' },
      { itemId: 'ancient_relic', x: 6000, y: 756, respawnMs: 30000, icon: '🏺', lore: 'The same coiled mark from the Vault catches pale light on the rising path.' },
      { itemId: 'ancient_relic', x: 7300, y: 596, respawnMs: 30000, icon: '🏺', lore: 'A field-worn relic waits beside the old highroad gate, pointed east.' },
    ],
  },
  'frostspire_peaks': {
    zoneId: 'frostspire_peaks',
    levelRange: [19, 25] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'rimecrest_troll', x: 1300, y: 1210, count: 3, respawnMs: 25000 },
      { mobId: 'rimecrest_troll', x: 1850, y: 1210, count: 2, respawnMs: 25000 },
      { mobId: 'rimecrest_troll', x: 2400, y: 1210, count: 2, respawnMs: 25000 },
      { mobId: 'wendigo', x: 900, y: 874, count: 1, respawnMs: 30000 },
      { mobId: 'wendigo', x: 2550, y: 994, count: 2, respawnMs: 30000 },
      { mobId: 'wendigo', x: 2800, y: 1114, count: 1, respawnMs: 30000 },
      { mobId: 'ice_wraith', x: 1100, y: 758, count: 2, respawnMs: 30000 },
      { mobId: 'ice_wraith', x: 1300, y: 638, count: 1, respawnMs: 30000 },
      { mobId: 'ice_wraith', x: 2280, y: 878, count: 2, respawnMs: 30000 },
      { mobId: 'voodoo_troll', x: 1850, y: 618, count: 2, respawnMs: 30000 },
      { mobId: 'voodoo_troll', x: 2050, y: 738, count: 2, respawnMs: 30000 },
      { mobId: 'mountain_troll', x: 2150, y: 736, count: 1, respawnMs: 40000 },
      { mobId: 'mountain_troll', x: 2820, y: 1096, count: 1, respawnMs: 40000 },
      { mobId: 'avalanche_golem', x: 1580, y: 459, count: 1, respawnMs: 300000 },
      { mobId: 'mountain_goat', x: 880, y: 880, count: 2, respawnMs: 25000 },
      { mobId: 'rimecrest_brakka', x: 1600, y: 1192, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'peaks_summitwarden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'peaks_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'frozen_lake': {
    zoneId: 'frozen_lake',
    levelRange: [6, 12] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'ice_drake', x: 520, y: 1150, count: 3, respawnMs: 35000 },
      { mobId: 'ice_drake', x: 1200, y: 1150, count: 2, respawnMs: 35000 },
      { mobId: 'crystal_spider', x: 800, y: 1150, count: 3, respawnMs: 25000 },
      { mobId: 'evil_eye', x: 1600, y: 850, count: 2, respawnMs: 20000 },
      { mobId: 'stone_golem', x: 2100, y: 1150, count: 2, respawnMs: 60000 },
      { mobId: 'snow_wolf', x: 1800, y: 1150, count: 3, respawnMs: 20000 },
      { mobId: 'frost_elemental', x: 2300, y: 650, count: 1, respawnMs: 60000 },
    ],
    npcSpawns: [],
  },
  'glowspore_grotto': {
    zoneId: 'glowspore_grotto',
    levelRange: [4, 6] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'mushroom', x: 500, y: 1250, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 1000, y: 1250, count: 1, respawnMs: 18000 },
      { mobId: 'stump', x: 1650, y: 1240, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 2150, y: 1240, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 2700, y: 815, count: 1, respawnMs: 18000 },
      { mobId: 'glowspore_keeper', x: 2620, y: 1016, count: 1, respawnMs: 60000 },
    ],
    npcSpawns: [
      { npcId: 'glowspore_orla', x: 250, y: 1250, facing: 'right' },
    ],
    collectibleSpawns: [
      { itemId: 'glowspore_field_notes', x: 950, y: 876, respawnMs: 30000, icon: '📖', displayName: 'Orla\'s Canopy Notes', lore: 'The lower caps dim before the crown wakes. The old colony can throw loose stone at anyone who mistakes the high shelves for a safe place to stand.' },
      { itemId: 'glowspore_heartseed', x: 2640, y: 826, respawnMs: 30000, icon: '🍄', displayName: 'Living Heartseed', lore: 'A pale pulse moves through the seed even when the rest of the crown is dark.' },
    ],
  },
  'goldengrass_savanna': {
    zoneId: 'goldengrass_savanna',
    levelRange: [3, 11] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'longleg_strider', x: 900, y: 1250, count: 3, respawnMs: 20000 },
      { mobId: 'tuskmire_raider', x: 1500, y: 1250, count: 3, respawnMs: 30000 },
      { mobId: 'tuskmire_raider', x: 2100, y: 1115, count: 1, respawnMs: 30000 },
      { mobId: 'tuskmire_warchief', x: 1700, y: 1084, count: 1, respawnMs: 300000 },
      { mobId: 'stripe_runner', x: 2700, y: 1250, count: 3, respawnMs: 25000 },
      { mobId: 'tuskmire_grunda', x: 1300, y: 962, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'savanna_outrider', x: 600, y: 1250, facing: 'right' },
      { npcId: 'savanna_trader', x: 360, y: 1250, facing: 'right' },
    ],
  },
  'lanternroot_hollow': {
    zoneId: 'lanternroot_hollow',
    levelRange: [4, 6] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'mushroom', x: 700, y: 1750, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 1100, y: 1750, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 1500, y: 1750, count: 1, respawnMs: 18000 },
      { mobId: 'stump', x: 1900, y: 1680, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 2400, y: 1680, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 2900, y: 1680, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 3200, y: 1680, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 3600, y: 1805, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 4100, y: 1805, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 4600, y: 1805, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 4900, y: 1805, count: 1, respawnMs: 18000 },
      { mobId: 'stump', x: 5300, y: 1700, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 5800, y: 1700, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 6300, y: 1790, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 6600, y: 1790, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 7000, y: 1725, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 7500, y: 1725, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 8000, y: 1815, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 8300, y: 1815, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 8600, y: 1735, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 9100, y: 1735, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 9600, y: 1735, count: 1, respawnMs: 18000 },
      { mobId: 'dark_stump', x: 9400, y: 1705, count: 1, respawnMs: 45000 },
    ],
    npcSpawns: [],
  },
  'lanternwood': {
    zoneId: 'lanternwood',
    levelRange: [4, 5] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'mushroom', x: 1500, y: 1480, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 1900, y: 1480, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 2400, y: 1480, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 2900, y: 1480, count: 1, respawnMs: 18000 },
      { mobId: 'stump', x: 3400, y: 1570, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 3900, y: 1570, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 4400, y: 1570, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 4900, y: 1570, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 5250, y: 1515, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 5500, y: 1515, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 6900, y: 1515, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 7150, y: 1515, count: 1, respawnMs: 18000 },
      { mobId: 'giant_bee', x: 7500, y: 1600, count: 1, respawnMs: 18000 },
      { mobId: 'giant_bee', x: 8100, y: 1690, count: 1, respawnMs: 18000 },
      { mobId: 'giant_bee', x: 8700, y: 1690, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 9300, y: 1590, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 9900, y: 1510, count: 1, respawnMs: 18000 },
      { mobId: 'mushroom', x: 10500, y: 1510, count: 1, respawnMs: 18000 },
      { mobId: 'stump', x: 11100, y: 1590, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 11700, y: 1590, count: 1, respawnMs: 22000 },
      { mobId: 'stump', x: 12300, y: 1590, count: 1, respawnMs: 22000 },
      { mobId: 'shadow_bat', x: 12800, y: 1535, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 13300, y: 1535, count: 1, respawnMs: 18000 },
      { mobId: 'shadow_bat', x: 13800, y: 1535, count: 1, respawnMs: 18000 },
    ],
    npcSpawns: [],
  },
  'moonfall_hollow': {
    zoneId: 'moonfall_hollow',
    levelRange: [1, 3] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'mushroom', x: 700, y: 1490, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 1100, y: 1490, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 1500, y: 1490, count: 1, respawnMs: 20000 },
      { mobId: 'slime', x: 1900, y: 1560, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 2500, y: 1560, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 3100, y: 1560, count: 1, respawnMs: 12000 },
      { mobId: 'snail', x: 3700, y: 1480, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 4300, y: 1570, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 4900, y: 1570, count: 1, respawnMs: 10000 },
      { mobId: 'mushroom', x: 5500, y: 1470, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 6000, y: 1560, count: 1, respawnMs: 20000 },
      { mobId: 'mushroom', x: 6500, y: 1560, count: 1, respawnMs: 20000 },
      { mobId: 'slime', x: 7100, y: 1490, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 7600, y: 1490, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 8100, y: 1490, count: 1, respawnMs: 12000 },
      { mobId: 'snail', x: 8700, y: 1550, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 9200, y: 1550, count: 1, respawnMs: 10000 },
      { mobId: 'snail', x: 9700, y: 1550, count: 1, respawnMs: 10000 },
      { mobId: 'moonfall_stone_golem', x: 7800, y: 1434, count: 1, respawnMs: 60000 },
      { mobId: 'snail', x: 7600, y: 1250, count: 1, respawnMs: 6000 },
    ],
    npcSpawns: [
      { npcId: 'rim_keeper', x: 560, y: 650, facing: 'left' },
    ],
  },
  'moonfen_pasture': {
    zoneId: 'moonfen_pasture',
    levelRange: [2, 4] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'snail', x: 1900, y: 1620, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 2400, y: 1620, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 3000, y: 1620, count: 1, respawnMs: 9000 },
      { mobId: 'pig', x: 3600, y: 1710, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 4100, y: 1710, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 4600, y: 1800, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 4900, y: 1800, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 5300, y: 1710, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 5800, y: 1630, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 6300, y: 1630, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 6500, y: 1630, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 7000, y: 1730, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 7500, y: 1730, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 8100, y: 1730, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 8600, y: 1650, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 9000, y: 1650, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 9400, y: 1650, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 9800, y: 1650, count: 1, respawnMs: 11000 },
    ],
    npcSpawns: [
      { npcId: 'moonfen_reedwarden', x: 520, y: 710, facing: 'right' },
      { npcId: 'moonfen_beacon_keeper', x: 9560, y: 1240, facing: 'left' },
    ],
    collectibleSpawns: [
      { itemId: 'moonfen_reedwater_tally', x: 2600, y: 1406, respawnMs: 30000, icon: '📜', displayName: 'Reedwater Tally', lore: 'Every pig counted through the reedwater; a second mark beside the ones that needed carrying.' },
      { itemId: 'moonfen_grange_ledger', x: 4780, y: 1346, respawnMs: 30000, icon: '📖', displayName: 'Herdkeeper\'s Ledger', lore: 'Take the dry boards east. Keep one lamp for the watchtower.' },
      { itemId: 'moonfen_last_watch_letter', x: 9900, y: 1146, respawnMs: 30000, icon: '✉️', displayName: 'Last Watch Letter', lore: 'Orren will keep the watch below. Leave the light where a tired traveller can see it.' },
    ],
  },
  'murkwater_swamp': {
    zoneId: 'murkwater_swamp',
    levelRange: [11, 17] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'gilling_coastrunner', x: 1400, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'gilling_coastrunner', x: 1250, y: 995, count: 1, respawnMs: 25000 },
      { mobId: 'gilling_coastrunner', x: 1850, y: 1235, count: 2, respawnMs: 25000 },
      { mobId: 'murkwater_snapjaw', x: 2500, y: 1260, count: 3, respawnMs: 30000 },
      { mobId: 'murkwater_snapjaw', x: 2100, y: 910, count: 1, respawnMs: 30000 },
      { mobId: 'gilling_bogchief', x: 2100, y: 1210, count: 1, respawnMs: 300000 },
      { mobId: 'moss_lurker', x: 2450, y: 1022, count: 2, respawnMs: 30000, level: 14, hp: 356, xp: 117 },
      { mobId: 'forest_spider', x: 2770, y: 1133, count: 2, respawnMs: 30000, level: 15, hp: 395, xp: 125 },
      { mobId: 'gilling_glimmergill', x: 1700, y: 998, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'swamp_bogwarden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'swamp_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'old_grove': {
    zoneId: 'old_grove',
    levelRange: [7, 9] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'forest_hollow_crown', x: 6208, y: 1648, count: 1, respawnMs: 60000 },
      { mobId: 'stump', x: 688, y: 1604, count: 1, respawnMs: 25000 },
      { mobId: 'wild_boar', x: 1248, y: 1678, count: 1, respawnMs: 20000 },
      { mobId: 'stump', x: 1792, y: 1668, count: 1, respawnMs: 25000 },
      { mobId: 'shadow_bat', x: 2144, y: 1693, count: 1, respawnMs: 24000 },
      { mobId: 'stump', x: 2528, y: 1732, count: 1, respawnMs: 25000 },
      { mobId: 'wild_boar', x: 3072, y: 1742, count: 1, respawnMs: 20000 },
      { mobId: 'shadow_bat', x: 3712, y: 1661, count: 1, respawnMs: 24000 },
      { mobId: 'shadow_bat', x: 4224, y: 1661, count: 1, respawnMs: 24000 },
      { mobId: 'stump', x: 8448, y: 1604, count: 1, respawnMs: 25000 },
    ],
    npcSpawns: [
      { npcId: 'forest_grovekeeper_senn', x: 7360, y: 1614, facing: 'right' },
    ],
  },
  'pig_beach': {
    zoneId: 'pig_beach',
    levelRange: [1, 3] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'snail', x: 1750, y: 1540, count: 3, respawnMs: 8000 },
      { mobId: 'jellyfish', x: 2450, y: 1540, count: 3, respawnMs: 10000 },
      { mobId: 'slime', x: 3500, y: 1520, count: 3, respawnMs: 10000 },
      { mobId: 'pig', x: 4620, y: 1470, count: 2, respawnMs: 12000 },
      { mobId: 'pig', x: 5240, y: 1470, count: 2, respawnMs: 12000 },
      { mobId: 'pig', x: 6060, y: 1530, count: 2, respawnMs: 12000 },
      { mobId: 'pig', x: 6820, y: 1530, count: 1, respawnMs: 12000 },
    ],
    npcSpawns: [
      { npcId: 'beach_naturalist', x: 900, y: 1490, facing: 'right' },
    ],
    collectibleSpawns: [
      { itemId: 'beach_trinket', x: 420, y: 1516, respawnMs: 25000, icon: '🐚' },
      { itemId: 'beach_trinket', x: 2200, y: 1556, respawnMs: 25000, icon: '🐚' },
      { itemId: 'beach_trinket', x: 3950, y: 1536, respawnMs: 25000, icon: '🐚' },
      { itemId: 'beach_trinket', x: 5400, y: 1496, respawnMs: 25000, icon: '🐚' },
      { itemId: 'beach_trinket', x: 6800, y: 1556, respawnMs: 25000, icon: '🐚' },
      { itemId: 'beach_trinket', x: 4820, y: 906, respawnMs: 25000, icon: '🐚' },
    ],
  },
  'pve-arena': {
    zoneId: 'pve-arena',
    levelRange: [1, 4] as [number, number],
    zoneType: 'arena',
    spawns: [
      { mobId: 'pig', x: 800, y: 1640, count: 3, respawnMs: 12000 },
      { mobId: 'snail', x: 1100, y: 1640, count: 2, respawnMs: 10000 },
      { mobId: 'slime', x: 1800, y: 1640, count: 3, respawnMs: 12000 },
      { mobId: 'mushroom', x: 3000, y: 1640, count: 2, respawnMs: 18000 },
      { mobId: 'mushroom', x: 3600, y: 1640, count: 2, respawnMs: 18000 },
      { mobId: 'pig', x: 4400, y: 1640, count: 3, respawnMs: 12000 },
      { mobId: 'jellyfish', x: 5000, y: 1640, count: 2, respawnMs: 14000 },
      { mobId: 'slime', x: 5500, y: 1640, count: 2, respawnMs: 12000 },
    ],
    npcSpawns: [],
  },
  'scorched_dunes': {
    zoneId: 'scorched_dunes',
    levelRange: [22, 28] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'giant_stag_beetle', x: 1000, y: 1262, count: 2, respawnMs: 20000 },
      { mobId: 'tomb_scarab', x: 1300, y: 1245, count: 3, respawnMs: 25000 },
      { mobId: 'tomb_scarab', x: 1190, y: 935, count: 2, respawnMs: 25000 },
      { mobId: 'tomb_scarab', x: 1700, y: 1245, count: 3, respawnMs: 25000 },
      { mobId: 'naga_siren', x: 1560, y: 1035, count: 2, respawnMs: 30000 },
      { mobId: 'centaur_scout', x: 2100, y: 1235, count: 3, respawnMs: 30000 },
      { mobId: 'centaur_scout', x: 2140, y: 1015, count: 2, respawnMs: 30000 },
      { mobId: 'naga_siren', x: 2460, y: 905, count: 3, respawnMs: 30000 },
      { mobId: 'cursed_archaeologist', x: 3060, y: 1202, count: 1, respawnMs: 300000 },
      { mobId: 'centaur_sorrel', x: 2680, y: 992, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'dunes_sandwarden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'dunes_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'smoldering_wastes': {
    zoneId: 'smoldering_wastes',
    levelRange: [14, 22] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'lava_slug', x: 1300, y: 1260, count: 3, respawnMs: 20000, level: 13, hp: 319, xp: 109 },
      { mobId: 'flame_imp', x: 1150, y: 880, count: 2, respawnMs: 25000, level: 14, hp: 356, xp: 117 },
      { mobId: 'flame_imp', x: 1560, y: 1000, count: 3, respawnMs: 25000, level: 14, hp: 356, xp: 117 },
      { mobId: 'flame_imp', x: 1700, y: 1250, count: 2, respawnMs: 25000, level: 14, hp: 356, xp: 117 },
      { mobId: 'young_fire_drake', x: 2050, y: 1248, count: 3, respawnMs: 30000 },
      { mobId: 'young_fire_drake', x: 2310, y: 998, count: 2, respawnMs: 30000 },
      { mobId: 'obsidian_sentinel', x: 2350, y: 1215, count: 1, respawnMs: 120000, level: 17, hp: 958, xp: 353 },
      { mobId: 'magma_golem', x: 2680, y: 840, count: 1, respawnMs: 90000, level: 16, hp: 872, xp: 333 },
      { mobId: 'infernal', x: 2650, y: 1180, count: 1, respawnMs: 300000, level: 20, hp: 3100, xp: 1485 },
      { mobId: 'fire_drake_ashkettle', x: 1250, y: 448, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'wastes_ashwarden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'wastes_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'starter-map': {
    zoneId: 'starter-map',
    levelRange: [1, 5] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'pig', x: 600, y: 400, count: 3, respawnMs: 15000 },
      { mobId: 'pig', x: 1000, y: 400, count: 2, respawnMs: 15000 },
      { mobId: 'snail', x: 800, y: 400, count: 2, respawnMs: 10000 },
      { mobId: 'slime', x: 1400, y: 400, count: 2, respawnMs: 15000 },
      { mobId: 'pig_mom', x: 1800, y: 400, count: 1, respawnMs: 60000 },
    ],
    npcSpawns: [],
  },
  'stormbreak_cliffs': {
    zoneId: 'stormbreak_cliffs',
    levelRange: [13, 19] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'stone_drake', x: 1300, y: 1245, count: 3, respawnMs: 25000 },
      { mobId: 'stone_drake', x: 1850, y: 1245, count: 2, respawnMs: 25000 },
      { mobId: 'stone_drake', x: 2400, y: 1245, count: 2, respawnMs: 25000 },
      { mobId: 'harpy_windcaller', x: 1300, y: 988, count: 3, respawnMs: 25000 },
      { mobId: 'harpy_windcaller', x: 1750, y: 988, count: 1, respawnMs: 25000 },
      { mobId: 'stormbreak_wyvern', x: 2100, y: 835, count: 1, respawnMs: 30000 },
      { mobId: 'stormbreak_wyvern', x: 2350, y: 705, count: 2, respawnMs: 30000 },
      { mobId: 'stormbreak_wyvern', x: 2600, y: 835, count: 1, respawnMs: 30000 },
      { mobId: 'stormbreak_wyvern', x: 2650, y: 975, count: 1, respawnMs: 30000 },
      { mobId: 'harpy_matriarch', x: 2400, y: 409, count: 1, respawnMs: 300000 },
      { mobId: 'stormbreak_galeclaw', x: 2040, y: 562, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'cliffs_beaconkeeper', x: 600, y: 1250, facing: 'right' },
      { npcId: 'cliffs_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'sunken_ruins': {
    zoneId: 'sunken_ruins',
    levelRange: [15, 20] as [number, number],
    zoneType: 'dungeon',
    spawns: [
      { mobId: 'crystal_spider', x: 500, y: 1550, count: 3, respawnMs: 25000 },
      { mobId: 'cursed_armor', x: 1000, y: 1550, count: 3, respawnMs: 90000 },
      { mobId: 'void_wraith', x: 1500, y: 1050, count: 1, respawnMs: 180000 },
      { mobId: 'gem_golem', x: 2000, y: 1550, count: 2, respawnMs: 90000 },
      { mobId: 'obsidian_sentinel', x: 2400, y: 950, count: 2, respawnMs: 120000 },
      { mobId: 'phoenix', x: 2800, y: 750, count: 1, respawnMs: 300000 },
      { mobId: 'ancient_treant', x: 1200, y: 1550, count: 2, respawnMs: 90000 },
      { mobId: 'war_golem', x: 2300, y: 1550, count: 1, respawnMs: 120000 },
    ],
    npcSpawns: [],
  },
  'sunkissed_shore': {
    zoneId: 'sunkissed_shore',
    levelRange: [16, 22] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'sandstinger', x: 1400, y: 1256, count: 3, respawnMs: 20000 },
      { mobId: 'sandstinger', x: 1700, y: 1136, count: 1, respawnMs: 20000 },
      { mobId: 'sandstinger', x: 1950, y: 1256, count: 2, respawnMs: 20000 },
      { mobId: 'basilisk', x: 2300, y: 1256, count: 2, respawnMs: 30000 },
      { mobId: 'basilisk', x: 2400, y: 1136, count: 1, respawnMs: 30000 },
      { mobId: 'basilisk', x: 2450, y: 876, count: 1, respawnMs: 30000 },
      { mobId: 'basilisk', x: 2750, y: 876, count: 1, respawnMs: 30000 },
      { mobId: 'murkwater_snapjaw', x: 2050, y: 1010, count: 2, respawnMs: 30000, level: 18, hp: 524, xp: 149 },
      { mobId: 'naga_siren', x: 2420, y: 725, count: 2, respawnMs: 30000, level: 21, hp: 671, xp: 173 },
      { mobId: 'leviathan', x: 2760, y: 1158, count: 1, respawnMs: 600000 },
      { mobId: 'sandstinger_sunback', x: 1215, y: 1127, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'shore_tidewatcher', x: 600, y: 1250, facing: 'right' },
      { npcId: 'shore_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'sunless_deep': {
    zoneId: 'sunless_deep',
    levelRange: [12, 20] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'kobold_worker', x: 1300, y: 1248, count: 3, respawnMs: 25000 },
      { mobId: 'kobold_worker', x: 1650, y: 1248, count: 3, respawnMs: 25000 },
      { mobId: 'kobold_tunneler', x: 1220, y: 838, count: 3, respawnMs: 25000 },
      { mobId: 'kobold_tunneler', x: 2000, y: 1248, count: 3, respawnMs: 25000 },
      { mobId: 'kobold_miner', x: 1600, y: 967, count: 3, respawnMs: 25000 },
      { mobId: 'deepglow_ooze', x: 1990, y: 1108, count: 2, respawnMs: 30000 },
      { mobId: 'deepglow_ooze', x: 2450, y: 968, count: 2, respawnMs: 30000 },
      { mobId: 'kobold_shaman', x: 2270, y: 1107, count: 1, respawnMs: 60000 },
      { mobId: 'rock_elemental', x: 2170, y: 815, count: 2, respawnMs: 30000 },
      { mobId: 'clodkin_stonecutter', x: 2350, y: 1247, count: 3, respawnMs: 25000 },
      { mobId: 'clodkin_stonecutter', x: 2700, y: 1247, count: 3, respawnMs: 25000 },
      { mobId: 'crystal_king', x: 2980, y: 1210, count: 1, respawnMs: 300000 },
      { mobId: 'clodkin_chertjaw', x: 2500, y: 676, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'deep_delver', x: 600, y: 1250, facing: 'right' },
      { npcId: 'deep_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'tidewend': {
    zoneId: 'tidewend',
    levelRange: [2, 8] as [number, number],
    zoneType: 'town',
    spawns: [],
    npcSpawns: [
      { npcId: 'elder_wynn', x: 1160, y: 950, facing: 'right' },
      { npcId: 'banker_barnaby', x: 1330, y: 950, facing: 'right' },
      { npcId: 'maren', x: 1510, y: 950, facing: 'left' },
      { npcId: 'sophia', x: 1980, y: 950, facing: 'left' },
      { npcId: 'blacksmith_erik', x: 2380, y: 950, facing: 'right' },
      { npcId: 'justiciar_kareth', x: 2540, y: 950, facing: 'right' },
      { npcId: 'captain_elowen', x: 2700, y: 950, facing: 'right' },
      { npcId: 'arcanist_vale', x: 2940, y: 950, facing: 'left' },
      { npcId: 'warden_rowan', x: 3200, y: 950, facing: 'right' },
      { npcId: 'whisper_tamsin', x: 3420, y: 950, facing: 'left' },
      { npcId: 'druid_elora', x: 3530, y: 950, facing: 'right' },
      { npcId: 'stormspeaker_iona', x: 3650, y: 950, facing: 'right' },
      { npcId: 'sister_vael', x: 3780, y: 950, facing: 'left' },
      { npcId: 'hexbinder_kane', x: 4250, y: 950, facing: 'left' },
      { npcId: 'old_ferris', x: 4100, y: 630, facing: 'left' },
      { npcId: 'guard_captain', x: 4680, y: 950, facing: 'right' },
    ],
    collectibleSpawns: [
      { itemId: 'fresh_fish', x: 3560, y: 976, respawnMs: 15000, icon: '🐟' },
      { itemId: 'fresh_fish', x: 3750, y: 976, respawnMs: 15000, icon: '🐟' },
      { itemId: 'fresh_fish', x: 3930, y: 976, respawnMs: 15000, icon: '🐟' },
      { itemId: 'fresh_fish', x: 4240, y: 976, respawnMs: 15000, icon: '🐟' },
      { itemId: 'fresh_fish', x: 4030, y: 656, respawnMs: 15000, icon: '🐟' },
    ],
  },
  'tidewend_outskirts': {
    zoneId: 'tidewend_outskirts',
    levelRange: [2, 3] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'pig', x: 1500, y: 1410, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 1900, y: 1410, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2300, y: 1410, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 2700, y: 1410, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 3400, y: 1500, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 3800, y: 1500, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 4200, y: 1500, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 4600, y: 1500, count: 1, respawnMs: 12000 },
      { mobId: 'pig', x: 5000, y: 1500, count: 1, respawnMs: 12000 },
      { mobId: 'slime', x: 6750, y: 1430, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 7000, y: 1430, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 7250, y: 1430, count: 1, respawnMs: 11000 },
      { mobId: 'snail', x: 7700, y: 1530, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 8100, y: 1530, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 8500, y: 1610, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 8900, y: 1610, count: 1, respawnMs: 9000 },
      { mobId: 'slime', x: 9600, y: 1450, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 10200, y: 1550, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 10800, y: 1550, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 11150, y: 1550, count: 1, respawnMs: 11000 },
      { mobId: 'snail', x: 11600, y: 1520, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 12200, y: 1520, count: 1, respawnMs: 9000 },
      { mobId: 'snail', x: 12800, y: 1520, count: 1, respawnMs: 9000 },
      { mobId: 'slime', x: 13400, y: 1470, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 13850, y: 1470, count: 1, respawnMs: 11000 },
      { mobId: 'slime', x: 14300, y: 1470, count: 1, respawnMs: 11000 },
    ],
    npcSpawns: [
      { npcId: 'pip_reedwhistle', x: 1050, y: 1470, facing: 'right' },
    ],
  },
  'tidewend_port': {
    zoneId: 'tidewend_port',
    levelRange: [1, 20] as [number, number],
    zoneType: 'field',
    mobFree: true,
    spawns: [],
    npcSpawns: [
      { npcId: 'tidewend_ferrymaster', x: 720, y: 950, facing: 'right' },
    ],
  },
  'tomb_waking': {
    zoneId: 'tomb_waking',
    levelRange: [1, 1] as [number, number],
    zoneType: 'dungeon',
    mobFree: true,
    spawns: [],
    npcSpawns: [],
    collectibleSpawns: [
      { itemId: 'waking_blade', x: 2260, y: 856, respawnMs: 30000, icon: '🗡️' },
      { itemId: 'waking_hood', x: 2410, y: 856, respawnMs: 30000, icon: '🎩' },
      { itemId: 'waking_tunic', x: 2560, y: 856, respawnMs: 30000, icon: '🥋' },
      { itemId: 'waking_leggings', x: 2710, y: 856, respawnMs: 30000, icon: '👖' },
      { itemId: 'waking_boots', x: 2860, y: 856, respawnMs: 30000, icon: '🥾' },
      { itemId: 'waking_gloves', x: 3010, y: 856, respawnMs: 30000, icon: '🧤' },
      { itemId: 'waking_cloak', x: 3160, y: 856, respawnMs: 30000, icon: '🧣' },
      { itemId: 'waking_charm', x: 3310, y: 856, respawnMs: 30000, icon: '🧿' },
      { itemId: 'gm_red_potion', x: 3760, y: 856, respawnMs: 30000, icon: '❤️' },
      { itemId: 'gm_blue_potion', x: 3910, y: 856, respawnMs: 30000, icon: '💙' },
      { itemId: 'gm_orange_potion', x: 4060, y: 856, respawnMs: 30000, icon: '🧡' },
      { itemId: 'gm_antidote', x: 4210, y: 856, respawnMs: 30000, icon: '🧪' },
      { itemId: 'gm_elixir_of_speed', x: 4360, y: 856, respawnMs: 30000, icon: '⚡' },
      { itemId: 'gm_elixir_of_fortitude', x: 4510, y: 856, respawnMs: 30000, icon: '🛡️' },
      { itemId: 'gm_flask_of_the_titans', x: 4660, y: 856, respawnMs: 30000, icon: '🏺' },
      { itemId: 'gm_power_elixir', x: 4810, y: 856, respawnMs: 30000, icon: '✨' },
    ],
  },
  'verdant_canopy': {
    zoneId: 'verdant_canopy',
    levelRange: [15, 21] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'moss_lurker', x: 1300, y: 1252, count: 3, respawnMs: 25000 },
      { mobId: 'thornpaw_pathfinder', x: 1700, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'thornpaw_pathfinder', x: 2100, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'forest_spider', x: 1300, y: 733, count: 2, respawnMs: 25000 },
      { mobId: 'canopy_weaver', x: 1660, y: 605, count: 3, respawnMs: 25000 },
      { mobId: 'canopy_weaver', x: 2200, y: 885, count: 2, respawnMs: 25000 },
      { mobId: 'venomous_spider_elite', x: 1960, y: 699, count: 1, respawnMs: 300000 },
      { mobId: 'owlbear', x: 2460, y: 995, count: 2, respawnMs: 30000 },
      { mobId: 'cave_bear', x: 2550, y: 1240, count: 3, respawnMs: 30000 },
      { mobId: 'darkwood_bear_elite', x: 2950, y: 1228, count: 1, respawnMs: 180000 },
      { mobId: 'thornpaw_mossclaw', x: 1200, y: 962, count: 1, respawnMs: 300000 },
    ],
    npcSpawns: [
      { npcId: 'canopy_ranger', x: 600, y: 1250, facing: 'right' },
      { npcId: 'canopy_trader', x: 340, y: 1250, facing: 'right' },
    ],
  },
  'village_arrival': {
    zoneId: 'village_arrival',
    levelRange: [1, 99] as [number, number],
    zoneType: 'town',
    spawns: [],
    npcSpawns: [
      { npcId: 'tomas', x: 900, y: 950, facing: 'right' },
      { npcId: 'merchant_maria', x: 1300, y: 950, facing: 'left' },
      { npcId: 'village_elder', x: 1100, y: 950, facing: 'left' },
    ],
  },
  'volcano_summit': {
    zoneId: 'volcano_summit',
    levelRange: [12, 20] as [number, number],
    zoneType: 'field',
    spawns: [
      { mobId: 'fire_boar', x: 500, y: 1550, count: 4, respawnMs: 30000 },
      { mobId: 'cursed_armor', x: 1000, y: 1550, count: 3, respawnMs: 90000 },
      { mobId: 'gem_golem', x: 1500, y: 1550, count: 2, respawnMs: 90000 },
      { mobId: 'dark_stump', x: 2000, y: 1550, count: 3, respawnMs: 45000 },
      { mobId: 'void_wraith', x: 2400, y: 950, count: 1, respawnMs: 180000 },
      { mobId: 'lava_slug', x: 1200, y: 1550, count: 4, respawnMs: 20000 },
      { mobId: 'magma_golem', x: 2200, y: 950, count: 2, respawnMs: 90000 },
      { mobId: 'infernal', x: 2600, y: 750, count: 1, respawnMs: 300000 },
      { mobId: 'flame_imp', x: 800, y: 1550, count: 3, respawnMs: 25000 },
      { mobId: 'obsidian_sentinel', x: 1800, y: 1150, count: 1, respawnMs: 120000 },
      { mobId: 'phoenix', x: 2700, y: 700, count: 1, respawnMs: 300000 },
      { mobId: 'void_walker', x: 1600, y: 1550, count: 2, respawnMs: 30000 },
      { mobId: 'phoenix', x: 2800, y: 950, count: 1, respawnMs: 600000 },
      { mobId: 'flame_imp', x: 1100, y: 1550, count: 3, respawnMs: 18000 },
    ],
    npcSpawns: [],
  },
  'wickhaven': {
    zoneId: 'wickhaven',
    levelRange: [6, 20] as [number, number],
    zoneType: 'town',
    spawns: [],
    npcSpawns: [
      { npcId: 'wickhaven_banker', x: 250, y: 950, facing: 'right' },
      { npcId: 'wickhaven_grocer', x: 470, y: 950, facing: 'right' },
      { npcId: 'wickhaven_smith', x: 690, y: 950, facing: 'right' },
      { npcId: 'wickhaven_lamplighter', x: 940, y: 950, facing: 'left' },
      { npcId: 'wickhaven_scholar', x: 1160, y: 950, facing: 'left' },
      { npcId: 'wickhaven_warden', x: 1380, y: 950, facing: 'left' },
    ],
  },
  'wickhaven_port': {
    zoneId: 'wickhaven_port',
    levelRange: [1, 20] as [number, number],
    zoneType: 'field',
    mobFree: true,
    spawns: [],
    npcSpawns: [
      { npcId: 'wickhaven_ferrymaster', x: 720, y: 950, facing: 'right' },
    ],
  },
  'windswept_moor': {
    zoneId: 'windswept_moor',
    levelRange: [6, 14] as [number, number],
    zoneType: 'field',
    draft: true,
    spawns: [
      { mobId: 'brigand_cutpurse', x: 1250, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'brigand_cutpurse', x: 1050, y: 985, count: 1, respawnMs: 25000 },
      { mobId: 'brigand_footpad', x: 1500, y: 985, count: 3, respawnMs: 25000 },
      { mobId: 'bandit_leader_grix', x: 1250, y: 855, count: 1, respawnMs: 90000 },
      { mobId: 'brigand_thug', x: 1700, y: 1235, count: 3, respawnMs: 25000 },
      { mobId: 'brigand_smuggler', x: 1950, y: 1115, count: 2, respawnMs: 30000 },
      { mobId: 'brigand_smuggler', x: 1900, y: 985, count: 1, respawnMs: 30000 },
      { mobId: 'brigand_raider', x: 2300, y: 1115, count: 2, respawnMs: 30000 },
      { mobId: 'brigand_raider', x: 2200, y: 1235, count: 2, respawnMs: 30000 },
      { mobId: 'brigand_lieutenant', x: 2550, y: 965, count: 2, respawnMs: 60000 },
      { mobId: 'brigand_lieutenant', x: 2650, y: 1215, count: 1, respawnMs: 60000 },
      { mobId: 'captain_morrow_vane', x: 2950, y: 1215, count: 1, respawnMs: 120000 },
    ],
    npcSpawns: [
      { npcId: 'moor_warden', x: 600, y: 1250, facing: 'right' },
      { npcId: 'moor_quartermaster', x: 330, y: 1250, facing: 'right' },
    ],
  },
};

export function getZoneConfig(zoneId: string): ZoneConfig | null {
  return ZONE_CONFIGS[zoneId] || null;
}

export function getZoneSpawns(zoneId: string): ZoneSpawnEntry[] {
  return ZONE_CONFIGS[zoneId]?.spawns || [];
}

export function getZoneNpcSpawns(zoneId: string): ZoneNpcSpawnEntry[] {
  return ZONE_CONFIGS[zoneId]?.npcSpawns || [];
}

export function getZoneCollectibleSpawns(zoneId: string): ZoneCollectibleSpawnEntry[] {
  return ZONE_CONFIGS[zoneId]?.collectibleSpawns || [];
}
